import { ConfirmationToken, PaymentIntent } from "@stripe/stripe-js";
import { Lead } from "../contexts/heyFlowLead";
import { PurchaseInformation } from "../contexts/purchase";
import { formatDate } from "../utils/date";
import { PaymentStatus } from "../contexts/payment";

import { Spinner } from "./Spinner";
import { PaymentMethodPreview } from "./PaymentMethodPreview";
import { COUNTRIES } from "./CountrySelect";
import { Link } from "react-router-dom";
import { Button } from "./Button";
import { Headline } from "./Headline";
import configuration from "../configuration";

// Horsia: Schritte-Folge nach erfolgreicher Bestellung. Texte im Du-Format.
const POST_PURCHASE_STEPS: { step: number; title: string; description: string }[] = [
  {
    step: 1,
    title: "Wir melden uns gleich telefonisch",
    description:
      "Dein persönlicher Ansprechpartner ruft dich kurzfristig an, um alle weiteren Schritte direkt mit dir zu besprechen.",
  },
  {
    step: 2,
    title: "Antrag auf Ausnahmegenehmigung",
    description:
      "Du erhältst von uns den Antrag auf Ausnahmegenehmigung, der vom zuständigen Veterinäramt gefordert wird. Diesen benötigen wir ausgefüllt und von dir sowie deinem Tierarzt unterschrieben zurück.",
  },
  {
    step: 3,
    title: "Genehmigung durch das Veterinäramt",
    description:
      "Das Veterinäramt erteilt die Ausnahmegenehmigung für den Transport und die Einäscherung deines Pferdes, sodass wir alle weiteren Schritte organisieren können.",
  },
  {
    step: 4,
    title: "Würdevolle Einzelabholung deines Pferdes",
    description:
      "Wir organisieren die behutsame Einzelabholung deines Pferdes und bieten dir somit auch eine ganz besondere Abschiedssituation. Anschließend erfolgt die Überführung ins Pferdekrematorium.",
  },
  {
    step: 5,
    title: "Pferdeeinäscherung & Ascherückgabe",
    description:
      "Nach der Kremierung erhältst du die Asche in deiner Wunschurne sowie alle weiteren Leistungen wie Hufabdruck oder Erinnerungsschmuckstücke zurück.",
  },
];

function PostPurchaseSteps() {
  return (
    <div className="mt-10 mb-8">
      <Headline title="So geht es weiter" type="sub" className="mb-5" />
      <div className="space-y-3">
        {POST_PURCHASE_STEPS.map(({ step, title, description }) => (
          <div key={step} className="bg-neutral-100 rounded-lg p-5 lg:p-6">
            <div className="flex items-start gap-4 lg:gap-5">
              <div className="shrink-0 w-9 h-9 rounded-full bg-green text-white flex items-center justify-center font-semibold">
                {step}
              </div>
              <div className="grow">
                <div className="font-semibold mb-1 text-base lg:text-lg">{title}</div>
                <div className="text-sm lg:text-base text-slate-600">{description}</div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

interface Props {
  paymentStatus: PaymentStatus | undefined;
  lead: Lead;
  heyflowId: string;
  purchaseInformation: PurchaseInformation;
  paymentIntent: PaymentIntent | undefined;
  paymentMethodPreview: ConfirmationToken.PaymentMethodPreview | undefined;
  paymentMethodPreviewIsLoading: boolean;
  isInvoiceMode?: boolean;
}

export function StripeComplete({
  lead,
  heyflowId,
  purchaseInformation,
  paymentIntent,
  paymentStatus,
  paymentMethodPreview,
  paymentMethodPreviewIsLoading,
  isInvoiceMode,
}: Props) {
  // Bei Invoice-Mode: kein Stripe-paymentIntent nötig; behandeln wie "success"
  if (!isInvoiceMode && !paymentIntent) return null;

  const country =
    COUNTRIES.find((country) => country.value === purchaseInformation.invoiceAddressCountry)?.text ??
    purchaseInformation.invoiceAddressCountry;
  const vorsorge = lead.vorsorge;

  const effectiveStatus: PaymentStatus = isInvoiceMode ? "success" : paymentStatus!;
  const effectiveDate = isInvoiceMode ? Date.now() : (paymentIntent!.created * 1000);

  return (
    <div>
      {effectiveStatus === "success" && (
        <>
          <div className="lg:pt-6">
            {vorsorge ? (
              <>
                Deine Vorsorge {vorsorge.planNumber} ist eingelöst und dein Auftrag für die Bestattung von{" "}
                {purchaseInformation.animalName} ist gut bei uns angekommen. Dein angespartes Guthaben wurde
                vollständig angerechnet. Dein persönlicher Ansprechpartner wird sich gleich mit dir in Verbindung
                setzen, um alle weiteren Details zu besprechen.
              </>
            ) : (
              <>
                Dein Auftrag für die Bestattung von {purchaseInformation.animalName} ist gut bei uns angekommen. Dein
                persönlicher Ansprechpartner wird sich gleich mit dir in Verbindung setzen, um alle weiteren Details zu
                besprechen.
              </>
            )}
          </div>
          <div className="mt-7">
            Auftragsnummer: {`#${lead.offerId}`}
            <br />
            Datum: {formatDate(effectiveDate)}
            <br />
            <span className="flex gap-3 items-center">
              Bezahlmethode:{" "}
              {isInvoiceMode ? (
                <span>Auf Rechnung (Überweisung nach Erhalt)</span>
              ) : paymentMethodPreviewIsLoading || paymentMethodPreview === undefined ? (
                <Spinner size="small" />
              ) : (
                <PaymentMethodPreview paymentMethodPreview={paymentMethodPreview} />
              )}
            </span>
            <br />
          </div>
          <div className="mt-4 mb-2">
            Kundeninformationen:
            <div className="text-sm">
              {`${purchaseInformation.invoiceAddressFirstName} ${purchaseInformation.invoiceAddressLastName}`}
              <br />
              {purchaseInformation.invoiceAddressStreet}
              <br />
              {purchaseInformation.invoiceAddressZipCode} {purchaseInformation.invoiceAddressCity}
              <br />
              {country}
            </div>
          </div>

          <PostPurchaseSteps />
          {vorsorge && (
            <a href={`${configuration.vorsorge.url}/konto`}>
              <Button caption="Zurück zu deinem Vorsorge-Konto" fullWidth={true} primary={true} large={true}></Button>
            </a>
          )}
        </>
      )}
      {effectiveStatus === "pending" && (
        <div className="flex items-center gap-4 pt-6">
          Deine Zahlung wird gerade bearbeitet. Bitte warte einen Moment. <Spinner size="small" />
        </div>
      )}
      {effectiveStatus === "cancelled" && (
        <div className="pt-6">
          <div className="mb-4">Deine Zahlung wurde abgrebochen.</div>
          <Link to={`/${heyflowId}/purchase`}>
            <Button caption="Zurück zum Formular" fullWidth={true} primary={true} large={true}></Button>
          </Link>
        </div>
      )}
      {effectiveStatus === "error" && (
        <div className="pt-6">
          <div className="mb-4">Es ist ein Fehler mit deiner Zahlung aufgetreten.</div>
          <Link to={`/${heyflowId}/purchase`}>
            <Button caption="Zurück zum Formular" fullWidth={true} primary={true} large={true}></Button>
          </Link>
        </div>
      )}
    </div>
  );
}
